import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, DotsThreeVertical } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiErrors";
import { Field, Modal } from "@/pages/Merchants";

const emptyForm = () => ({
  name: "",
  form_factor: "virtual",
  currency: "USD",
  description: "",
});

function cardToForm(c) {
  return {
    name: c.name || "",
    form_factor: c.form_factor || "virtual",
    currency: c.currency || "USD",
    description: c.description || "",
  };
}

export default function CardTypes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(
    () => api.get("/admin/card-types").then((r) => setItems(r.data.items || [])),
    [],
  );
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm());
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm(cardToForm(c));
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      description: form.description.trim() || undefined,
    };
    try {
      if (editing) {
        await api.patch(`/admin/card-types/${editing.id}`, payload);
        toast.success("Card type updated");
      } else {
        await api.post("/admin/card-types", payload);
        toast.success("Card type created");
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed"));
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/card-types/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Card type disabled" : "Card type enabled");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed"));
    }
  };

  // Delete disabled — use Disable in the actions menu instead (same as soft-deactivating a programme).
  // const remove = async (c) => {
  //   if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
  //   try {
  //     await api.delete(`/admin/card-types/${c.id}`);
  //     toast.success("Card type deleted");
  //     load();
  //   } catch (err) {
  //     toast.error(apiErrorMessage(err, "Could not delete"));
  //   }
  // };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="card-types-page">
        <PageHeader
          title="Card Types"
          subtitle="Define the card programmes merchants can issue against."
          actions={
            <button
              type="button"
              onClick={openCreate}
              data-testid="create-card-type-btn"
              className="h-9 px-4 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150"
            >
              <Plus size={14} weight="bold" /> New card type
            </button>
          }
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <Surface key={c.id} className="p-5" testId={`card-type-${c.id}`}>
              <div className="flex items-start justify-between gap-2 mb-4">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{c.form_factor}</div>
                  <div className="font-display text-xl tracking-tight font-medium mt-0.5">{c.name}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusPill value={c.active ? "active" : "suspended"} />
                  <CardTypeActionsMenu
                    cardType={c}
                    open={openMenuId === c.id}
                    onOpenChange={(next) => setOpenMenuId(next ? c.id : null)}
                    onEdit={() => { setOpenMenuId(null); openEdit(c); }}
                    onToggle={() => { setOpenMenuId(null); toggleActive(c); }}
                    // onDelete={() => { setOpenMenuId(null); remove(c); }}
                  />
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <KV k="Currency" v={c.currency} />
              </div>
              {c.description && <p className="text-xs text-zinc-500 mt-4">{c.description}</p>}
            </Surface>
          ))}
          {items.length === 0 && (
            <Surface className="col-span-full p-10 text-center text-sm text-zinc-500">No card types defined yet.</Surface>
          )}
        </div>

        {open && (
          <Modal title={editing ? "Edit card type" : "New card type"} onClose={closeModal}>
            <form onSubmit={submit} className="space-y-3">
              <Field label="Name" testId="ct-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Form factor</label>
                  <select
                    data-testid="ct-form-factor"
                    value={form.form_factor}
                    onChange={(e) => setForm({ ...form, form_factor: e.target.value })}
                    className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
                  >
                    <option value="virtual">Virtual</option>
                    <option value="physical">Physical</option>
                  </select>
                </div>
                <Field label="Currency" testId="ct-currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} />
              </div>
              <Field label="Description" testId="ct-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150">Cancel</button>
                <button type="submit" data-testid="submit-ct" className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 transition-colors duration-150">
                  {editing ? "Save changes" : "Create"}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </Layout>
  );
}

function CardTypeActionsMenu({ cardType, open, onOpenChange, onEdit, onToggle /* , onDelete */ }) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const placeMenu = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 160;
    const menuHeight = 120;
    const gap = 4;
    let top = rect.bottom + gap;
    let left = rect.right - menuWidth;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - gap);
    }
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    setMenuStyle({ position: "fixed", top, left, width: menuWidth, zIndex: 50 });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [open, placeMenu]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      onOpenChange(false);
    };
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="Card type actions"
        aria-expanded={open}
        data-testid={`card-type-actions-${cardType.id}`}
        onClick={() => onOpenChange(!open)}
        className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors duration-150"
      >
        <DotsThreeVertical size={18} weight="bold" />
      </button>
      {open && menuStyle && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={menuStyle}
          className="min-w-[10rem] rounded-sm border border-zinc-200 bg-white py-1 shadow-lg"
          data-testid={`card-type-actions-menu-${cardType.id}`}
        >
          <MenuItem onClick={onEdit} testId={`edit-card-type-${cardType.id}`}>Edit</MenuItem>
          <MenuItem onClick={onToggle} testId={`toggle-card-type-${cardType.id}`}>
            {cardType.active ? "Disable" : "Enable"}
          </MenuItem>
          {/* <MenuItem onClick={onDelete} testId={`delete-card-type-${cardType.id}`} destructive>
            Delete
          </MenuItem> */}
        </div>,
        document.body,
      )}
    </>
  );
}

function MenuItem({ children, onClick, testId, destructive }) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      className={`block w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 transition-colors duration-150 ${
        destructive ? "text-red-700" : "text-zinc-900"
      }`}
    >
      {children}
    </button>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-zinc-500">{k}</span>
      <span className="font-mono tabular-nums">{v}</span>
    </div>
  );
}
